import { copyFile, readFile } from '@ps/api/editor/file'
import { projectBasicPath } from '@ps/workbench/config'
import store from '@ps/store'
import { IframeWindow } from '@ps/bridge/iframe/iframe.window.type'
import { IGlobalConfigBg } from '@ps/bridge/qc/scripts/bg-config'
import { Node } from 'qc'
import { isImage } from '@ps/bridge/qc/base'
import _ from 'lodash'
import { getScript } from '@ps/bridge/qc/scripts/edit-text'
import { specialNodes } from '@ps/bridge/qc/node/config'
import router from '@ps/router'
import { each } from '@ps/bridge/qc/node/utils'
import filepath from '@ps/bridge/path'
import qc from 'qc'
import * as globalConfigScript from '@ps/bridge/qc/scripts/bg-config'
import { getFileExtension } from '@com/utils/file'
import { findTargetResource } from '@ps/bridge/resource'

function isQcDom(node: any): node is qc.Dom {
  return node.div && node.div.ELEMENT_NODE === 1
}

const bgOriginName = 'pb_bg_origin'

/**
 * 复制一份图片，作为背景图的原路径，保证滤镜每次都是在原图的基础上滤镜
 */
export async function copyBgImg({
  dst,
  src,
  pid
}: {
  src: string
  dst: string
  pid: number
}) {
  await copyFile({
    pid,
    src,
    dst,
    overwrite: 1
  })
  const originResource = `${projectBasicPath}${pid}/${dst}`
  await store.dispatch('workspace/editorConfig/updateTargetNodeConfig', {
    originalResource: originResource,
    resource: originResource,
    bg: true
  })
}

/**
 * 从素材包资产列表中查找背景图片
 */
async function getBgImgInfoFromAssets(qc: any): Promise<{
  url: string;
  // ext 后缀名包含 . 号，如 .png
  ext: string;
} | void> {
  const projectId = Number(router.currentRoute.query.id)
  const { gameOb } = qc
  const uuid2url = gameOb.assets._uuid2UrlConf as Record<string, string>
  const assetUUid = Object.keys(uuid2url).find(key =>
    /(?<!ending)(\/pb_bg\.bin)$/i.test(uuid2url[key])
  )
  if (assetUUid) {
    const bgImgBinUrl = uuid2url[assetUUid]
    const res = (await readFile({
      pid: projectId,
      path: bgImgBinUrl
    })) as Array<string>
    const source = res
      .filter(Boolean)
      .map(item => JSON.parse(item))
      .find(item => !!item.source).source
    const ext = (source && source[0]) || '.jpg'
    return {
      url: bgImgBinUrl.replace('.bin', ext),
      ext
    }
  }
}

/**
 * 获取背景图的资源路径
 */
async function getBgUrl(
  globalConfig: IGlobalConfigBg,
  qc: any
): Promise<string | void> {
  const projectId = Number(router.currentRoute.query.id)
  const res = await store.dispatch('workspace/resource/getProjectResource', {
    projectKey: projectId
  })
  const bgUrlInScript = globalConfig.bgUrl
  // 接口返回的链接是以 / 开头的
  if (bgUrlInScript) {
    const cur = findTargetResource(
      res.data.result,
      name => bgUrlInScript === _.trimStart(name, '/')
    )
    // 检查一下脚本上配置的资源是否存在
    if (cur) {
      console.log('找到脚本对应的背景图片')
      return _.trimStart(cur, '/')
    }
    console.log('脚本对应的背景图片不存在')
    const bgImgInfo = await getBgImgInfoFromAssets(qc)
    // 由于 globalConfig.bgUrl 可能与 bgResourceUrl 不一致
    // 如果 bgResourceUrl 不等于 globalConfig.bgUrl，则使用 bgResourceUrl
    if (
      bgImgInfo &&
        bgImgInfo.url &&
        findTargetResource(res.data.result, name => bgImgInfo.url === _.trimStart(name, '/'))
    ) {
      if (bgUrlInScript !== bgImgInfo.url) {
        console.log(
          'globalConfig.bgUrl 与在素材包检索出来的背景图片不一致，使用素材包检索出来的背景图片'
        )
        return _.trimStart(bgImgInfo.url, '/')
      }
    } else {
      console.log('没有在素材包检索出背景图片')
    }
  }
}

export async function adaptBgImgWhenGamePlayNodeHasGlobalConfigScript({
  globalConfig,
  qc
}: {
  globalConfig: IGlobalConfigBg
  qc: any
}) {
  const projectId = Number(router.currentRoute.query.id)
  const bgResourceUrl = await getBgUrl(globalConfig, qc)
  // 由于 globalConfig.bgUrl 可能与 bgResourceUrl 不一致，默认使用 bgResourceUrl
  if (bgResourceUrl) {
    globalConfig.bgUrl = bgResourceUrl
    const ext = getFileExtension(globalConfig.bgUrl)
    await copyBgImg({
      dst: `${filepath.TEXTURE_BASIC_PATH}${bgOriginName}.${ext}`,
      src: globalConfig.bgUrl,
      pid: projectId
    })
    return {
      hasBgImg: true
    }
  }
  console.log('==================== 没有找到背景图')
  return {
    hasBgImg: false
  }
}

/**
 * 在 _uuid2UrlConf 中找到背景图片(pb_bg.bin)的路径，后续更新则直接覆盖该图片
 */
export async function adaptBgImgWhenGameNodeHasNotGlobalConfigScript({
  iframeWindow,
  qc
}: {
  iframeWindow: IframeWindow
  qc: any
}): Promise<{
  globalConfig: IGlobalConfigBg
  hasBgImg: boolean
} | void> {
  // 删除所有引用背景图片的相关节点
  function findBgImageNode(node: Node, assetUUid: string) {
    if (isImage(node)) {
      const atlas = _.get(node, 'texture.atlas')
      if (atlas && atlas.uuid === assetUUid) {
        const nodeHadOtherScripts = (node: Node): boolean => {
          if (node.scripts && node.scripts.length) {
            return node.scripts.some(script => {
              return script.class !== 'qc.AspectRatioFitter'
            })
          }
          return false
        }
        // 如果当前背景图片含有脚本，代表背景图片的节点含有逻辑，不可以随意删除
        if (
          node.children &&
          node.children.length === 0 &&
          !nodeHadOtherScripts(node)
        ) {
          node.removeSelf()
        } else {
          // 添加这个脚本之后，设置 visible 将会不影响持久化的值
          // 为什么要这样搞？因为没有删除节点的话，节点会一直存在于场景，而更新了背景图片的时候，虽然更新了节点引用的资源路径的图片，但是游戏画面还是沿用缓存的图片，导致编辑器看不到实时效果
          getScript(node)
          node.visible = false
        }
      }
    }
  }

  console.log(
    '查找不在 ending 环节下的背景图片，并将其该图片的信息添加到 ps.GlobalConfigBg 脚本中'
  )

  const { UIRoot } = iframeWindow
  const { gameOb } = qc
  const gamePlayNode = UIRoot.getChild(specialNodes.gamePlay.name)
  // 无 ps.GlobalConfigBg 脚本，添加脚本
  const globalConfig = globalConfigScript.getScript(
    gamePlayNode,
    true
  ) as IGlobalConfigBg
  const projectId = Number(router.currentRoute.query.id)
  const uuid2url = gameOb.assets._uuid2UrlConf as Record<string, string>
  // 查找不在 ending 环节下的背景图片
  const assetUUid = Object.keys(uuid2url).find(key =>
    /(?<!ending)(\/pb_bg\.bin)$/i.test(uuid2url[key])
  )

  if (assetUUid) {
    // 可能会存在多个背景图片，所以需要遍历所有背景图片
    const assetUUidArray = Object.keys(uuid2url).filter(key =>
      /(\/pb_bg\.bin)$/i.test(uuid2url[key])
    )
    // 背景图片可能就是一个qc.Image节点,则将的 texture 设置为透明图片
    each(gamePlayNode, (node: Node) => {
      assetUUidArray.forEach(id => {
        findBgImageNode(node, id)
      })
    })

    const bgImgInfo = await getBgImgInfoFromAssets(qc)
    if (bgImgInfo) {
      const bgImgUrl = bgImgInfo.url
      const ext = bgImgInfo.ext
      await copyBgImg({
        dst: `${filepath.TEXTURE_BASIC_PATH}${bgOriginName}${ext}`,
        src: bgImgUrl,
        pid: projectId
      })

      if (isQcDom(gamePlayNode)) {
        globalConfig.bgUrl = bgImgUrl
        globalConfig.initBg()
      }
      // 保存图片地址
      // 理论上不存在这种情况
      if (!isQcDom(gamePlayNode)) {
        store.commit('workspace/globalConfig/updateBgUrl', { url: bgImgUrl })
      }
      return {
        globalConfig,
        hasBgImg: true
      }
    }
  }
  console.log('==================== 没有找到背景图')
}
